import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Lightbulb, MessageSquare, Wrench } from 'lucide-react';
import { Action, Chip, ErrorNote, Loading, PageBody, Panel } from '../../components/aa';
import { useAsync } from '../aai/useAsync';
import { LANE_TONE, adoptionApi, isClientView, laneLabel, shortDate } from './adoption';
import type { CompanyGraph, Opportunity } from './adoption';
import { useRoot } from './CompanyAdoption';
import { NODE_COLOR } from './GraphMap';

const KIND_LABEL: Record<Opportunity['kind'], string> = {
  problem: 'Opportunity',
  request: 'Request',
  tool: 'Tool opportunity',
};

const KIND_ICON: Record<Opportunity['kind'], React.ReactNode> = {
  problem: <Lightbulb size={20} aria-hidden />,
  request: <MessageSquare size={20} aria-hidden />,
  tool: <Wrench size={20} aria-hidden />,
};

const REL_TEXT: Record<string, { out: string; in: string }> = {
  created: { out: 'Created', in: 'Raised by' },
  for_department: { out: 'For the team', in: 'Opportunity' },
  mentions: { out: 'Mentions the tool', in: 'Mentioned in' },
  about_tool: { out: 'About the tool', in: 'Opportunity about it' },
  became: { out: 'Became the project', in: 'Came from' },
};

const TOOL_STATUS: Record<string, string> = {
  connected: 'connected',
  not_connected: 'used, not connected',
  not_in_catalog: 'not connectable yet',
};

interface Link2 {
  key: string;
  phrase: string;
  label: string;
  kind: string;
  note: string;
  color: string;
}

const connections = (graph: CompanyGraph | null, nodeKey: string): Link2[] => {
  if (!graph) return [];
  const byKey: Record<string, CompanyGraph['nodes'][number]> = {};
  graph.nodes.forEach((n) => { byKey[n.key] = n; });
  const out: Link2[] = [];
  graph.edges.forEach((e) => {
    const outward = e.src === nodeKey;
    const inward = e.dst === nodeKey;
    if (!outward && !inward) return;
    let otherKey = e.src;
    if (outward) otherKey = e.dst;
    const other = byKey[otherKey];
    if (!other) return;
    const text = REL_TEXT[e.rel] || { out: e.rel, in: e.rel };
    let phrase = text.in;
    if (outward) phrase = text.out;
    let note = '';
    if (other.kind === 'tool') note = TOOL_STATUS[String(other.props.status || '')] || '';
    if (other.kind === 'person' && other.props.role) note = String(other.props.role);
    if (other.kind === 'project' && other.props.status) note = String(other.props.status).replace(/_/g, ' ').toLowerCase();
    out.push({ key: `${e.rel}-${otherKey}`, phrase, label: other.label, kind: other.kind, note, color: NODE_COLOR(other.kind, other.props.status) });
  });
  return out;
};

const OpportunityPage: React.FC = () => {
  const root = useRoot();
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId = '', kind = 'problem', opportunityId = '' } = useParams<{ companyId: string; kind: string; opportunityId: string }>();
  const loaded = useAsync(() => adoptionApi.company(companyId), 'Could not load this opportunity.', [companyId]);
  const graph = useAsync<CompanyGraph>(() => adoptionApi.graph(companyId), '', [companyId]);

  const back = () => {
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    navigate(`${root}/${companyId}?tab=opportunities`);
  };

  if (loaded.loading && !loaded.data) return <PageBody><Loading label="Loading the opportunity" /></PageBody>;
  if (loaded.error || !loaded.data) return <PageBody><ErrorNote message={loaded.error || 'Could not load this opportunity.'} /></PageBody>;

  const { company, viewer } = loaded.data;
  const clientView = isClientView(viewer);
  const item = company.opportunities.find((o) => o.id === opportunityId && o.kind === kind);
  if (!item) {
    return (
      <PageBody>
        <ErrorNote message="This opportunity is not here any more, or you cannot see it." />
        <div className="mt-4"><Action variant="quiet" to={`${root}/${companyId}?tab=opportunities`}>Back to {company.name}</Action></div>
      </PageBody>
    );
  }

  const facts: { label: string; value: React.ReactNode }[] = [
    { label: 'Status', value: <Chip tone={LANE_TONE[item.lane]} dot>{laneLabel(item.lane, clientView)}</Chip> },
    { label: 'Created by', value: item.created_by?.name || 'Not recorded' },
  ];
  if (item.created_by?.role) facts.push({ label: 'Their role', value: item.created_by.role });
  facts.push({ label: 'Came from', value: item.source });
  facts.push({ label: 'Company', value: <Link to={`${root}/${company.id}`} className="font-semibold text-ink underline-offset-2 hover:underline">{company.name}</Link> });
  if (item.department) facts.push({ label: 'Team', value: item.department });
  if (item.created_at) facts.push({ label: 'Created', value: shortDate(item.created_at) });
  if (item.updated_at && item.updated_at !== item.created_at) facts.push({ label: 'Last changed', value: shortDate(item.updated_at) });

  const links = connections(graph.data, `opportunity:${item.kind}:${item.id}`);
  let statement = item.statement;
  if (statement === item.title) statement = '';

  return (
    <PageBody>
      <button type="button" onClick={back} className="mb-5 inline-flex min-h-[32px] items-center gap-1.5 text-[13px] font-semibold text-ink-muted hover:text-ink">
        <ArrowLeft size={15} aria-hidden />
        Back
      </button>

      <div className="mb-6 flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-tile bg-canvas-inset text-ink-muted">{KIND_ICON[item.kind]}</span>
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[11.5px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{KIND_LABEL[item.kind]} · {company.name}</p>
          <h1 className="m-0 mt-1.5 text-[24px] font-bold leading-[31px] tracking-[-0.01em] text-ink sm:text-[28px] sm:leading-[36px]">{item.title}</h1>
          <div className="mt-3"><Chip tone={LANE_TONE[item.lane]} dot>{laneLabel(item.lane, clientView)}</Chip></div>
        </div>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <Panel>
            <div className="px-6 py-5">
              <h2 className="m-0 text-[15px] font-bold text-ink">What it is about</h2>
              {statement && <p className="m-0 mt-2 whitespace-pre-line text-[14.5px] leading-[24px] text-ink">{statement}</p>}
              {!statement && <p className="m-0 mt-2 text-[14px] text-ink-dim">No more detail than the title yet.</p>}
            </div>
          </Panel>
          <Panel>
            <div className="border-b border-line-soft px-6 py-4">
              <h2 className="m-0 text-[15px] font-bold text-ink">Connected to</h2>
              <p className="m-0 mt-0.5 text-[12.5px] text-ink-dim">From the company knowledge graph: who raised it, the team, the tools it mentions and what it became.</p>
            </div>
            {graph.loading && !graph.data && <div className="px-6 py-5"><Loading label="Reading the knowledge graph" /></div>}
            {!graph.loading && links.length === 0 && <p className="m-0 px-6 py-5 text-[13px] text-ink-dim">Nothing else in the map links to it yet.</p>}
            {links.length > 0 && (
              <ul className="m-0 list-none divide-y divide-line-soft p-0">
                {links.map((l) => (
                  <li key={l.key} className="flex items-center gap-3 px-6 py-3">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: l.color }} aria-hidden />
                    <span className="w-[150px] shrink-0 text-[12.5px] text-ink-dim">{l.phrase}</span>
                    <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-ink">{l.label}</span>
                    {l.note && <span className="hidden shrink-0 text-[12px] text-ink-muted sm:inline">{l.note}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel>
            <dl className="m-0 divide-y divide-line-soft">
              {facts.map((f) => (
                <div key={f.label} className="flex items-center justify-between gap-4 px-5 py-3">
                  <dt className="text-[13px] text-ink-dim">{f.label}</dt>
                  <dd className="m-0 text-right text-[13px] font-semibold text-ink">{f.value}</dd>
                </div>
              ))}
            </dl>
          </Panel>
          {clientView && item.kind !== 'tool' && (
            <Action to={`/projects?tab=opportunities&open=${item.id}`} arrow className="w-full">Open in Projects</Action>
          )}
        </div>
      </div>
    </PageBody>
  );
};

export default OpportunityPage;
